/**
 * @fileoverview Aggregate product-level metrics across all countries for hovercards.
 * @module shared/hooks/dashboard/useAggregatedProductMetrics
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'

export interface AggregatedProductMetrics {
  /** Dimension key for product */
  sku: string
  /** Human readable product name */
  name: string
  /** Units sold */
  quantity: number
  /** Sales revenue */
  sales: number
  /** Advertising spend */
  adSpend: number
  /** Platform breakdown of advertising spend */
  adPlatformBreakdown: Record<string, number>
  /** Cost of goods sold */
  cogs: number
  /** Net profit */
  profit: number
}

export interface UseAggregatedProductMetricsResult {
  products: AggregatedProductMetrics[]
  isLoading: boolean
  isError: boolean
  error: string | null
}

/**
 * Aggregates product metrics from the product-level dashboard query across all rows.
 * This is intentionally lightweight and avoids any UI assumptions.
 */
export function useAggregatedProductMetrics(): UseAggregatedProductMetricsResult {
  const { salesProductQuery } = useDashboardData()
  const { productLabel } = useLabelResolver()

  const dimString = (d: Record<string, unknown> | undefined, key: string): string | undefined => {
    if (!d) return undefined
    const v = d[key as keyof typeof d]
    return typeof v === 'string' ? v : undefined
  }

  const products = useMemo<AggregatedProductMetrics[]>(() => {
    const rows = salesProductQuery.data?.data || []
    if (rows.length === 0) return []

    const map = new Map<string, AggregatedProductMetrics>()

    rows.forEach(row => {
      const sku = dimString(row.dimensions as Record<string, unknown>, 'product')
      if (!sku) return

      const existing = map.get(sku) || {
        sku,
        name: productLabel(sku),
        quantity: 0,
        sales: 0,
        adSpend: 0,
        adPlatformBreakdown: {} as Record<string, number>,
        cogs: 0,
        profit: 0,
      }

      const revenue = row.metrics.sales || 0
      const ad = row.metrics.advertising || 0
      const cogs = row.metrics.cogs || 0
      const direct = row.metrics.directCosts || 0
      const cm2 =
        typeof row.metrics.cm2 === 'number' ? row.metrics.cm2 : revenue - (ad + cogs + direct)

      existing.quantity += row.metrics.quantity || 0
      existing.sales += revenue
      existing.adSpend += ad
      existing.cogs += cogs
      existing.profit += cm2

      const breakdown = row.metrics.platformBreakdown || {}
      Object.entries(breakdown).forEach(([platform, amount]) => {
        existing.adPlatformBreakdown[platform] =
          (existing.adPlatformBreakdown[platform] || 0) + amount
      })

      map.set(sku, existing)
    })

    return Array.from(map.values())
  }, [salesProductQuery.data, productLabel])

  return {
    products,
    isLoading: salesProductQuery.isLoading,
    isError: salesProductQuery.isError,
    error: salesProductQuery.error?.message || null,
  }
}
